import React from 'react';
import { Alert, Box, Typography, Paper, IconButton } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import BlockIcon from '@mui/icons-material/Block';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CloseIcon from '@mui/icons-material/Close';

/**
 * Component to display account status notifications to users
 * @param {Object} props - Component props
 * @param {string} props.status - The account status (active, dormant, closed, disabled, suspicious, frozen, irs)
 */
const AccountStatusBanner = ({ status }) => {
  // If status is active or undefined, don't show any banner
  if (!status || status === 'active') {
    return null;
  }

  // Define configuration for different status types
  const statusConfig = {
    dormant: {
      message: 'Sorry, your account is dormant/inactive, please contact customer care info@safenetbanking.com',
      color: '#fffbe5', // Light yellow background
      textColor: '#856404', // Dark yellow/amber text
      borderColor: '#ffeeba', // Light amber border
    },
    closed: {
      message: 'Sorry, your account is closed, please contact customer care info@safenetbanking.com',
      color: '#fffbe5', // Light yellow background
      textColor: '#856404', // Dark yellow/amber text
      borderColor: '#ffeeba', // Light amber border
    },
    disabled: {
      message: 'Sorry, your account is disabled, please contact customer care info@safenetbanking.com',
      color: '#fffbe5', // Light yellow background
      textColor: '#856404', // Dark yellow/amber text
      borderColor: '#ffeeba', // Light amber border
    },
    suspicious: {
      message: 'Sorry, your account has been flagged for suspicious activity, please contact customer care info@safenetbanking.com',
      color: '#fffbe5', // Light yellow background
      textColor: '#856404', // Dark yellow/amber text
      borderColor: '#ffeeba', // Light amber border
    },
    frozen: {
      message: 'Sorry, your account is frozen, please contact customer care info@safenetbanking.com',
      color: '#fffbe5', // Light yellow background
      textColor: '#856404', // Dark yellow/amber text
      borderColor: '#ffeeba', // Light amber border
    },
    irs: {
      message: 'Sorry, your account has an IRS hold, please contact customer care info@safenetbanking.com',
      color: '#fffbe5', // Light yellow background
      textColor: '#856404', // Dark yellow/amber text
      borderColor: '#ffeeba', // Light amber border
    }
  };

  // Get config for current status or use a default
  const config = statusConfig[status] || {
    message: `Sorry, your account status is ${status}, please contact customer care info@safenetbanking.com`,
    color: '#fffbe5', // Light yellow background
    textColor: '#856404', // Dark yellow/amber text
    borderColor: '#ffeeba', // Light amber border
  };

  const [visible, setVisible] = React.useState(true);

  if (!visible) return null;

  return (
    <Box sx={{ 
      width: '100%', 
      position: 'relative', 
      zIndex: 10,
      mb: 2,
      mt: 0
    }}>
      <Paper 
        elevation={0}
        sx={{
          bgcolor: config.color,
          color: config.textColor,
          py: 1.5,
          px: 2,
          border: `1px solid ${config.borderColor}`,
          borderRadius: 0,
          width: '100%',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Typography variant="body1" sx={{ pr: 3 }}>
          {config.message}
        </Typography>
        <IconButton 
          size="small" 
          sx={{ color: config.textColor }} 
          onClick={() => setVisible(false)}
          aria-label="close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Paper>
    </Box>
  );
};

export default AccountStatusBanner;
